import { ref, runTransaction, get } from 'firebase/database';

/**
 * Equipment rental availability is tracked per calendar date, independently
 * of court slotLocks, in two small Firebase nodes:
 *
 *   equipmentHolds/{dateStr}/{equipmentId}/{bookingId} = { qty, startHour, endHour, expiresAt }
 *     - One entry per booking that rented this equipment type, holding
 *       `qty` units for the [startHour, endHour) span of its session (the
 *       earliest to latest hour across all of that booking's selected time
 *       slots — see getBookingHourRange). Mirrors slotLocks/{date} exactly,
 *       including the expiresAt/promote lifecycle (see bookingAvailabilityUtil.js).
 *
 *   equipmentManualUsage/{dateStr}/{equipmentId}/{hour} = number
 *     - Units the admin has manually marked as out for walk-in rentals
 *       during that specific hour (not tied to an online booking) — set per
 *       the time slot(s) selected on the admin Equipment screen, the same
 *       way a court's booked hour is specific rather than a flat day total.
 *       Saving a value for a multi-slot selection writes that same qty to
 *       every hour in the selection.
 *
 * Both nodes are scoped to a single date, so every read/write here touches
 * a small, bounded slice of data regardless of how large the overall
 * `bookings` table grows — no full-table scans.
 */

const isEquipmentHoldExpired = (hold) => {
  if (!hold) return true;
  if (!hold.expiresAt) return false;
  return new Date(hold.expiresAt).getTime() < Date.now();
};

/**
 * Sums qty across every bookingId hold entry for one equipment type into a
 * 24-length array of units in use per hour of the day. A hold with
 * startHour=13, endHour=16 (1pm-4pm) contributes its qty to hours 13,14,15.
 *
 * @param {Object} holdsForEquipment - equipmentHolds/{date}/{equipmentId} value.
 * @param {string|null} excludeBookingId - a hold to skip, if any.
 * @returns {number[]} 24-length array; hourly[h] = units in use during hour h.
 */
export const computeHourlyEquipmentUsage = (holdsForEquipment, excludeBookingId = null) => {
  const hourly = new Array(24).fill(0);
  Object.entries(holdsForEquipment || {}).forEach(([bookingId, hold]) => {
    if (!hold || bookingId === excludeBookingId || isEquipmentHoldExpired(hold)) return;
    const start = Math.max(0, Number(hold.startHour) || 0);
    const end = Math.min(24, Number(hold.endHour) || 0);
    for (let h = start; h < end; h++) hourly[h] += Number(hold.qty) || 0;
  });
  return hourly;
};

/**
 * Derives the [startHour, endHour) span an equipment rental should be held
 * for, from the guest's selected court time slots — the earliest slot's
 * start hour to the latest slot's end hour (each slot is one hour long).
 * Falls back to a 1-hour window at hour 0 if selectedSlots is empty.
 *
 * @param {Object} selectedSlots - { timeSlot: [court, ...] }
 * @param {(timeSlot: string) => number} parseTimeSlotStartHour
 */
export const getBookingHourRange = (selectedSlots, parseTimeSlotStartHour) => {
  const hours = Object.keys(selectedSlots || {}).map(parseTimeSlotStartHour);
  if (hours.length === 0) return { startHour: 0, endHour: 1 };
  return { startHour: Math.min(...hours), endHour: Math.max(...hours) + 1 };
};

const inventoryTotalFor = (config, equipmentId) => {
  const def = (config?.equipment || []).find(e => e.id === equipmentId);
  return Number(def?.inventoryTotal) || 0;
};

// Busiest hour's worth of manually-logged walk-in usage within
// [startHour, endHour) — manualUsageForItem is equipmentManualUsage/{date}/{equipmentId},
// a { [hour]: qty } map (see the module doc comment above).
const manualUsagePeak = (manualUsageForItem, startHour, endHour) => {
  let peak = 0;
  for (let h = startHour; h < endHour; h++) {
    peak = Math.max(peak, Number(manualUsageForItem?.[h]) || 0);
  }
  return peak;
};

/**
 * One-shot read of how many units of each equipment type are still free
 * for a proposed [startHour, endHour) window on `dateStr` — two small reads
 * (equipmentHolds/{date}, equipmentManualUsage/{date}), not a scan of
 * `bookings`. Used by EquipmentRentalModal to show live "X available" and
 * cap the quantity pickers; the actual claim (see claimEquipmentRental) is
 * what enforces this for real at booking-creation time.
 *
 * @returns {Promise<Object>} { [equipmentId]: unitsRemaining }
 */
export const fetchEquipmentAvailability = async (db, dateStr, startHour, endHour, config) => {
  const availability = {};
  const equipmentList = config?.equipment || [];
  if (!db || !dateStr) {
    equipmentList.forEach(({ id }) => { availability[id] = inventoryTotalFor(config, id); });
    return availability;
  }

  const [holdsSnap, manualSnap] = await Promise.all([
    get(ref(db, `equipmentHolds/${dateStr}`)),
    get(ref(db, `equipmentManualUsage/${dateStr}`)),
  ]);
  const holds = holdsSnap.exists() ? holdsSnap.val() : {};
  const manualUsage = manualSnap.exists() ? manualSnap.val() : {};

  equipmentList.forEach(({ id }) => {
    const total = inventoryTotalFor(config, id);
    const hourly = computeHourlyEquipmentUsage(holds[id]);
    let busiest = 0;
    for (let h = startHour; h < endHour; h++) busiest = Math.max(busiest, hourly[h] || 0);
    const manualBusiest = manualUsagePeak(manualUsage[id], startHour, endHour);
    availability[id] = Math.max(0, total - manualBusiest - busiest);
  });
  return availability;
};

/**
 * Atomically claims equipment quantities for a booking, one Firebase
 * transaction per equipment type in `items` (equipment types are
 * independent of each other, so each gets its own transaction on
 * equipmentHolds/{dateStr}/{equipmentId} — same reasoning as
 * claimBookingSlots: the transaction's read-check-write happens as one
 * indivisible operation, so two guests racing for the last few units of the
 * same equipment during overlapping times can't both succeed).
 *
 * If any equipment type in the request can't be fully claimed, whatever was
 * already claimed for this bookingId in earlier iterations is rolled back —
 * a booking never ends up with a partial equipment rental.
 *
 * An item may carry its OWN `startHour`/`endHour`, which override the
 * booking-wide span for that item alone. That is how a "per hour" rental
 * (Config -> Equipment -> Rental type) is held only for the hours the guest
 * actually picked, instead of tying units up across a whole session they
 * aren't paying for — see App.jsx's createPendingBooking, which derives them
 * from the item's chosen slots. A gap in that pick (6-7PM and 9-10PM but not
 * the hours between) still holds the whole span: the hold node stores one
 * range per booking, and over-holding is the safe direction to err in.
 *
 * @param {Object} db
 * @param {string} dateStr
 * @param {Array<{id: string, qty: number, startHour?: number, endHour?: number}>} items - from EquipmentRentalModal's selection.
 * @param {string} bookingId
 * @param {number} startHour - default span, used by any item without its own
 * @param {number} endHour
 * @param {Object} config - admin config (for each item's inventory total).
 * @param {string|null} expiresAt
 * @returns {Promise<{granted: boolean, shortItem: {id: string, qty: number}|null}>}
 */
export const claimEquipmentRental = async (db, dateStr, items, bookingId, startHour, endHour, config, expiresAt = null) => {
  if (!db || !items || items.length === 0) return { granted: true, shortItem: null };

  const manualSnap = await get(ref(db, `equipmentManualUsage/${dateStr}`));
  const manualUsage = manualSnap.exists() ? manualSnap.val() : {};

  const claimedIds = [];
  for (const item of items) {
    const totalInventory = inventoryTotalFor(config, item.id);
    const itemStart = Number.isFinite(item.startHour) ? item.startHour : startHour;
    const itemEnd = Number.isFinite(item.endHour) ? item.endHour : endHour;
    const manualForItem = manualUsagePeak(manualUsage[item.id], itemStart, itemEnd);
    const holdsRef = ref(db, `equipmentHolds/${dateStr}/${item.id}`);

    // eslint-disable-next-line no-await-in-loop
    const result = await runTransaction(holdsRef, (current) => {
      const data = current || {};
      const hourly = computeHourlyEquipmentUsage(data);
      let busiest = 0;
      for (let h = itemStart; h < itemEnd; h++) busiest = Math.max(busiest, hourly[h] || 0);
      const remaining = Math.max(0, totalInventory - manualForItem - busiest);
      // Returning undefined aborts the transaction — data is left
      // untouched, so a blocked attempt can never partially claim.
      if (item.qty > remaining) return undefined;
      return { ...data, [bookingId]: { qty: item.qty, startHour: itemStart, endHour: itemEnd, expiresAt } };
    });

    if (!result.committed) {
      // eslint-disable-next-line no-await-in-loop
      await Promise.all(claimedIds.map(id => releaseOneEquipmentHold(db, dateStr, id, bookingId).catch(() => {})));
      return { granted: false, shortItem: item };
    }
    claimedIds.push(item.id);
  }
  return { granted: true, shortItem: null };
};

const releaseOneEquipmentHold = async (db, dateStr, equipmentId, bookingId) => {
  const holdsRef = ref(db, `equipmentHolds/${dateStr}/${equipmentId}`);
  await runTransaction(holdsRef, (current) => {
    if (!current || !current[bookingId]) return current;
    const next = { ...current };
    delete next[bookingId];
    return next;
  });
};

/**
 * Releases every equipmentHolds entry owned by `bookingId` on `dateStr`.
 * Reads which equipment types actually have holds for this date first
 * (rather than looping a fixed equipment list — equipment is now an
 * admin-managed, per-sport array on config, and this helper has no config
 * in scope at most of its call sites), so it stays correct regardless of
 * how the admin's equipment list changes over time. Call this alongside
 * releaseBookingSlots whenever a booking stops holding its slot (guest
 * backs out, payment window lapses, admin declines/cancels).
 */
export const releaseEquipmentRental = async (db, dateStr, bookingId) => {
  if (!db || !dateStr || !bookingId) return;
  const holdsSnap = await get(ref(db, `equipmentHolds/${dateStr}`));
  if (!holdsSnap.exists()) return;
  const equipmentIds = Object.keys(holdsSnap.val() || {});
  await Promise.all(
    equipmentIds.map(id => releaseOneEquipmentHold(db, dateStr, id, bookingId).catch(() => {}))
  );
};

/**
 * Clears a booking's equipmentHolds expiry (mirrors promoteBookingSlots) —
 * call alongside it once the booking moves past the short payment
 * countdown, so the equipment stays held indefinitely rather than freeing
 * itself up mid-review. Same "read which equipment types are actually
 * held" approach as releaseEquipmentRental above, for the same reason.
 */
export const promoteEquipmentRental = async (db, dateStr, bookingId) => {
  if (!db || !dateStr || !bookingId) return;
  const holdsSnap = await get(ref(db, `equipmentHolds/${dateStr}`));
  if (!holdsSnap.exists()) return;
  const equipmentIds = Object.keys(holdsSnap.val() || {});
  await Promise.all(equipmentIds.map(async (id) => {
    const holdsRef = ref(db, `equipmentHolds/${dateStr}/${id}`);
    await runTransaction(holdsRef, (current) => {
      if (!current || !current[bookingId]) return current;
      const next = { ...current };
      next[bookingId] = { ...next[bookingId], expiresAt: null };
      return next;
    }).catch(() => {});
  }));
};
